import { Controller } from "@hotwired/stimulus"

// FD-397: fetch a turbo-frame's contents only when its <details> disclosure is
// first opened, instead of building them on every render. Unlike lazy-reveal
// (which clones inert <template> markup already in the DOM — so the server still
// BUILT it), this defers the *server work*: the frame carries no src until the
// first open, when we set it and Turbo fetches the endpoint once. Written for the
// add-relation item picker (a <cs-popover> since FD-1354, see below), whose option list
// is up to 200 of the project's work items — too costly to query on every work-item
// peek/detail render.
//
//   <details data-controller="frame-reveal" data-action="toggle->frame-reveal#reveal"
//            data-frame-reveal-src-value="/…/relations/item_options">
//     <summary>…</summary>
//     <turbo-frame id="…" data-frame-reveal-target="frame">…placeholder…</turbo-frame>
//   </details>
//
// FD-502: also drives the lazy property pickers (parent/labels/cycle), where the
// host is a popover (not a <details>) that dispatches popover:open on open, and —
// for the big lists — a server-side typeahead: an input wired to #search re-fetches
// the frame with ?q= (debounced) so the endpoint filters instead of the client.
//
// FD-501, FD-1298: also works on a <cs-popover> (the toolbar Filters menu): wire
// `cs-show->frame-reveal#reveal`. cs-show fires once `open` is already true, so the
// open check below lets it through; it only blocks a genuinely-closed <details>/<dialog>.
// FD-568: "Delete does nothing". A lazily-revealed frame carries a runtime `src` but no
// refresh="morph", and Turbo only exempts a frame from morphing when
// `src && refresh === "morph"` — so an FD-238 broadcast refresh (fired on EVERY commit in
// the project, 0.5s debounce) morphed the row's "…" menu frame back to its server-rendered
// spinner and stripped `open` from the host <details>. The Delete/Archive/Move button was
// removed from the DOM under the cursor; if the morph landed between mousedown and mouseup
// no click event fired at all — a perfect "the button did nothing".
//
// preventDefault() on the HOST <details> skips its whole subtree, covering both the `open`
// attribute and the loaded frame body in one guard (the documented FD-566 pattern —
// docs/solutions/best-practices/native-dialog-frozen-by-turbo-morph.md). refresh="morph" on
// the frame is NOT enough: the morph still strips <details open>. data-turbo-permanent is
// wrong: it freezes the rendered state permanently. The row's data goes stale while the menu
// is open — acceptable for a transient overlay; a closed menu morphs normally. The guard keys
// on an OPEN <details> host. popover_controller's hosts aren't <details> and are never
// preserved here; they manage their own open state. An open <cs-popover> host (the toolbar's
// Filters, the FD-397 relation picker since FD-1354) is kept by the page-wide guard in
// lib/cornerstone.js.
//
// Installed ONCE per page rather than per controller instance: a list can host 50+
// frame-reveal rows and turbo:before-morph-element fires for every element in the morphed
// tree, so a listener per row would multiply one refresh into thousands of callbacks.
let morphGuardInstalled = false

function installMorphGuard() {
  if (morphGuardInstalled) return
  morphGuardInstalled = true
  document.addEventListener("turbo:before-morph-element", (event) => {
    const el = event.target
    if (el instanceof HTMLDetailsElement && el.open &&
        el.matches("[data-controller~='frame-reveal']")) {
      event.preventDefault()
    }
  })
}

export default class extends Controller {
  static targets = ["frame", "search"]
  static values = { src: String }

  connect() {
    installMorphGuard()
  }

  disconnect() {
    clearTimeout(this._searchTimer)
  }

  reveal() {
    // <details> hosts fire toggle on close too — only act on open. Popover and
    // focus-dropdown hosts (no `open` property) only signal on open.
    if (this.element.open === false || !this.hasFrameTarget) return
    // Focus our typeahead input (popover-managed search inputs focus themselves).
    // FD-1190: it opens right under the trigger the user clicked; don't scroll to it.
    if (this.hasSearchTarget) requestAnimationFrame(() => this.searchTarget.focus({ preventScroll: true }))
    // Load once (Turbo fetches as soon as src is set, and the loaded options
    // persist across subsequent collapse/expand).
    if (this.frameTarget.getAttribute("src")) return
    this.frameTarget.setAttribute("src", this.srcValue)
  }

  // FD-956: re-fetch a loaded frame after a form inside the host succeeds (the epic
  // page's "Add existing items"). The morph guard above keeps an open host, and so its
  // now-stale options, out of the page refresh that the form's redirect triggers.
  refresh(event) {
    if (event?.detail?.success === false || !this.hasFrameTarget) return
    if (this.frameTarget.getAttribute("src")) this.frameTarget.reload()
  }

  // Server-side typeahead: re-point the frame at the endpoint with ?q=, debounced
  // so a burst of keystrokes costs one request.
  search() {
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => {
      const url = new URL(this.srcValue, window.location.href)
      const q = this.searchTarget.value.trim()
      if (q) url.searchParams.set("q", q)
      this.frameTarget.setAttribute("src", url.toString())
    }, 250)
  }
}
